import { useState, useEffect } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import type { FunctionDefinition } from '@/types/compatibility'
import { useToast } from '@/hooks/useToast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AgentProxyService } from '@/services/agentProxyService'
import {
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

interface EditFunctionPanelProps {
  connection: DatabaseConnection
  database: string
  functionName: string
  schema?: string
  initialFunction?: FunctionDefinition
  onClose: () => void
  onSuccess: () => void
}

interface FunctionParameter {
  name: string
  type: string
  mode?: 'IN' | 'OUT' | 'INOUT'
  defaultValue?: string
}

interface ParsedFunction {
  parameters: FunctionParameter[]
  returns: string
  body: string
  language: string
}

function parseFunctionDefinition(definition: string, dbType: 'postgresql' | 'mysql'): ParsedFunction {
  const result: ParsedFunction = {
    parameters: [],
    returns: 'void',
    body: '',
    language: 'plpgsql'
  }

  try {
    if (dbType === 'postgresql') {
      // Parse PostgreSQL function definition
      // Example: (length numeric, width numeric) RETURNS numeric AS $$ BEGIN ... END; $$ LANGUAGE plpgsql
      
      // Extract parameters - handle complex parameter lists
      const paramMatch = definition.match(/\(([\s\S]*?)\)\s+RETURNS/i)
      if (paramMatch) {
        const paramString = paramMatch[1].trim()
        if (paramString) {
          // Split by comma but be careful with nested parentheses (e.g., DEFAULT values)
          const params: string[] = []
          let current = ''
          let depth = 0
          
          for (const char of paramString) {
            if (char === '(' || char === '[') depth++
            else if (char === ')' || char === ']') depth--
            else if (char === ',' && depth === 0) {
              params.push(current.trim())
              current = ''
              continue
            }
            current += char
          }
          if (current.trim()) {
            params.push(current.trim())
          }
          
          for (const param of params) {
            // Handle parameter format: [mode] name type [DEFAULT value]
            // Examples:
            // - project_id integer
            // - new_status character varying
            // - OUT old_status character varying
            // - INOUT some_param text DEFAULT 'value'
            
            console.log('Processing parameter:', param)
            
            let paramStr = param
            let mode: 'IN' | 'OUT' | 'INOUT' = 'IN' // Default to IN
            
            // Check for mode prefix (IN/OUT/INOUT)
            const modeMatch = paramStr.match(/^(IN|OUT|INOUT)\s+/i)
            if (modeMatch) {
              const upperMode = modeMatch[1].toUpperCase()
              if (upperMode === 'IN' || upperMode === 'OUT' || upperMode === 'INOUT') {
                mode = upperMode
              }
              paramStr = paramStr.substring(modeMatch[0].length).trim()
              console.log(`Found mode: ${mode}, remaining: ${paramStr}`)
            }
            
            // Extract default value if present
            const defaultMatch = paramStr.match(/\s+DEFAULT\s+(.+)$/i)
            let defaultValue: string | undefined
            if (defaultMatch) {
              const [fullMatch, defaultVal] = defaultMatch
              defaultValue = defaultVal
              paramStr = paramStr.substring(0, paramStr.indexOf(fullMatch)).trim()
            }
            
            // Split remaining string into name and type
            // Handle special cases like "character varying"
            const firstSpaceIndex = paramStr.indexOf(' ')
            if (firstSpaceIndex > 0) {
              const name = paramStr.substring(0, firstSpaceIndex)
              const type = paramStr.substring(firstSpaceIndex + 1).trim()
              
              if (name && type) {
                console.log(`Adding parameter: name=${name}, type=${type}, mode=${mode}`)
                result.parameters.push({
                  name,
                  type,
                  mode,
                  defaultValue
                })
              }
            }
          }
        }
      }

      // Extract return type - handle both simple types and TABLE(...)
      const tableReturnsMatch = definition.match(/RETURNS\s+TABLE\s*\((.*?)\)/is)
      const simpleReturnsMatch = definition.match(/RETURNS\s+(\S+)/i)
      
      if (tableReturnsMatch) {
        // For TABLE return type, capture the full TABLE(...) definition
        const [, tableColumns] = tableReturnsMatch
        result.returns = `TABLE(${tableColumns})`
      } else if (simpleReturnsMatch) {
        const [, returnType] = simpleReturnsMatch
        result.returns = returnType
      }

      // Extract language first (needed for body processing)
      const langMatch = definition.match(/LANGUAGE\s+(\w+)/i)
      if (langMatch) {
        result.language = langMatch[1].toLowerCase()
      }

      // Extract body - handle both $$ and $function$ delimiters
      const bodyMatch = definition.match(/AS\s+\$(\w*)\$\s*(.*?)\s*\$\1\$/is)
      if (bodyMatch) {
        let body = bodyMatch[2].trim()
        console.log('Extracted body before processing:', body)
        
        // For plpgsql functions, keep the body as-is since it might have DECLARE sections
        // The body structure varies:
        // 1. Simple: BEGIN ... END;
        // 2. With DECLARE: DECLARE vars; BEGIN ... END;
        // 3. Complex nested structures
        
        if (result.language === 'plpgsql') {
          // Don't modify the body structure - let PostgreSQL handle it
          // Just clean up any extra whitespace
          body = body.trim()
        }
        
        console.log('Body after processing:', body)
        result.body = body
      }
    } else {
      // Parse MySQL function definition (basic support)
      result.language = 'sql'
      
      // For MySQL, the definition might be in a different format
      const paramMatch = definition.match(/^\s*\((.*?)\)/s)
      if (paramMatch) {
        const paramString = paramMatch[1].trim()
        if (paramString) {
          const params = paramString.split(',').map(p => p.trim())
          for (const param of params) {
            const parts = param.split(/\s+/)
            if (parts.length >= 2) {
              result.parameters.push({
                name: parts[0],
                type: parts.slice(1).join(' ')
              })
            }
          }
        }
      }

      const returnsMatch = definition.match(/RETURNS\s+(\S+)/i)
      if (returnsMatch) {
        const [, returnType] = returnsMatch
        result.returns = returnType
      }

      // Extract body between BEGIN and END
      const bodyMatch = definition.match(/BEGIN\s+(.*?)\s*END/is)
      if (bodyMatch) {
        result.body = bodyMatch[1].trim()
      }
    }
  } catch (error) {
    console.error('Error parsing function definition:', error)
  }

  return result
}

function buildFunctionDefinition(
  parameters: FunctionParameter[],
  returnType: string,
  body: string,
  language: string,
  dbType: 'postgresql' | 'mysql'
): string {
  if (dbType === 'postgresql') {
    // Build parameter string - include ALL parameters with their modes
    console.log('Building function with parameters:', parameters)
    const paramStrings = parameters.map(param => {
      let paramStr = ''
      
      // Always include the mode for OUT and INOUT parameters
      // For IN parameters, the mode is optional but we can include it for clarity
      if (param.mode === 'OUT' || param.mode === 'INOUT') {
        paramStr += `${param.mode} `
        console.log(`Adding ${param.mode} prefix for parameter ${param.name}`)
      }
      // Optionally include IN mode (PostgreSQL allows omitting it)
      // else if (param.mode === 'IN') {
      //   paramStr += 'IN '
      // }
      
      paramStr += `${param.name} ${param.type}`
      
      if (param.defaultValue) {
        paramStr += ` DEFAULT ${param.defaultValue}`
      }
      
      console.log(`Built param string: ${paramStr}`)
      return paramStr
    })
    
    const paramString = paramStrings.join(', ')
    console.log('Final param string:', paramString)
    
    // Handle body structure for plpgsql functions
    let finalBody = body
    if (language === 'plpgsql') {
      // Check if body already has a DECLARE section
      const hasDeclare = /^\s*DECLARE\s/i.test(body.trim())
      
      if (hasDeclare) {
        // For functions with DECLARE, the body should already have proper structure:
        // DECLARE ... BEGIN ... END;
        // Don't modify it to avoid nested BEGIN/END issues
        finalBody = body
        console.log('Function has DECLARE section, preserving original structure')
      } else {
        // For functions without DECLARE, check BEGIN/END structure
        const hasBegin = /^\s*BEGIN\s/i.test(body)
        const hasEnd = /\s*END\s*;?\s*$/i.test(body)
        
        if (!hasBegin && !hasEnd) {
          // Only add BEGIN/END if neither are present
          finalBody = `BEGIN\n${body}\nEND;`
        } else if (!hasBegin && hasEnd) {
          // Add BEGIN if only END is present
          finalBody = `BEGIN\n${body}`
        } else if (hasBegin && !hasEnd) {
          // Add END if only BEGIN is present
          finalBody = `${body}\nEND;`
        }
        // If both are present, use the body as-is
      }
    }
    
    // For PostgreSQL, use $$ delimiter (standard format)
    // Return the function definition without CREATE FUNCTION
    const dollarSign = '$'
    const startDelimiter = dollarSign + dollarSign  // Construct explicitly to avoid any template literal issues
    const endDelimiter = dollarSign + dollarSign
    const definition = `(${paramString}) RETURNS ${returnType} AS ${startDelimiter}\n${finalBody}\n${endDelimiter} LANGUAGE ${language}`
    console.log('Start delimiter:', startDelimiter)
    console.log('End delimiter:', endDelimiter)
    console.log('Final function definition:', definition)
    console.log('Definition length:', definition.length)
    console.log('Definition includes $$:', definition.includes('$$'))
    console.log('Start delimiter char codes:', Array.from(startDelimiter).map(c => c.charCodeAt(0)))
    console.log('End delimiter char codes:', Array.from(endDelimiter).map(c => c.charCodeAt(0)))
    return definition
  } else {
    // MySQL format
    const paramStrings = parameters.map(param => `${param.name} ${param.type}`)
    const paramString = paramStrings.join(', ')
    
    return `(${paramString}) RETURNS ${returnType} DETERMINISTIC BEGIN ${body} END`
  }
}

export function EditFunctionPanel({ 
  connection, 
  database, 
  functionName, 
  schema, 
  initialFunction,
  onClose,
  onSuccess 
}: EditFunctionPanelProps) {
  // Parse the initial function definition to extract components
  const initialParsed = initialFunction?.definition 
    ? parseFunctionDefinition(initialFunction.definition, connection.type)
    : { parameters: [], returns: 'void', body: '', language: 'plpgsql' }
  
  console.log('=== EditFunctionPanel initialization ===')
  console.log('initialFunction:', initialFunction)
  console.log('initialParsed:', initialParsed)

  const [parameters, setParameters] = useState<FunctionParameter[]>(initialParsed.parameters)
  const [returnType, setReturnType] = useState(initialParsed.returns)
  const [customReturnType, setCustomReturnType] = useState('')
  const [language, setLanguage] = useState(initialParsed.language)
  const [functionBody, setFunctionBody] = useState(initialParsed.body)
  const [isSaving, setIsSaving] = useState(false)
  
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const isDatabasePostgreSQL = connection.type === 'postgresql'
  const isDatabaseMySQL = connection.type === 'mysql'

  const commonDataTypes = isDatabasePostgreSQL
    ? ['integer', 'bigint', 'text', 'varchar', 'character varying', 'boolean', 'date', 'timestamp', 'numeric', 'json', 'jsonb']
    : ['INT', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'BOOLEAN', 'DATE', 'DATETIME', 'DECIMAL', 'JSON']

  const returnTypes = isDatabasePostgreSQL
    ? ['void', 'integer', 'bigint', 'text', 'varchar', 'boolean', 'date', 'timestamp', 'numeric', 'json', 'jsonb', 'record', 'TRIGGER', 'CUSTOM']
    : ['INT', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'BOOLEAN', 'DATE', 'DATETIME', 'DECIMAL', 'JSON']

  const availableLanguages = isDatabasePostgreSQL
    ? ['plpgsql', 'sql', 'plpython3u', 'plperl']
    : ['sql']

  useEffect(() => {
    if (initialFunction?.definition) {
      console.log('Parsing function definition:', initialFunction.definition)
      const parsed = parseFunctionDefinition(initialFunction.definition, connection.type)
      console.log('Parsed result:', parsed)
      console.log('Parsed parameters with modes:', parsed.parameters.map(p => ({
        name: p.name,
        type: p.type,
        mode: p.mode,
        defaultValue: p.defaultValue
      })))
      
      setParameters(parsed.parameters)
      
      // Check if it's a custom/complex return type
      const isCustom = parsed.returns.startsWith('TABLE(') || 
        (!['void', 'integer', 'bigint', 'text', 'varchar', 'boolean', 'date', 'timestamp', 'numeric', 'json', 'jsonb', 'record', 'TRIGGER',
          'INT', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'BOOLEAN', 'DATE', 'DATETIME', 'DECIMAL', 'JSON'].includes(parsed.returns))
      
      if (isCustom) {
        setReturnType('CUSTOM')
        setCustomReturnType(parsed.returns)
      } else {
        setReturnType(parsed.returns)
        setCustomReturnType('')
      }
      
      setLanguage(parsed.language)
      setFunctionBody(parsed.body)
    }
  }, [initialFunction, connection.type])

  // Monitor parameters state for debugging
  useEffect(() => {
    console.log('Parameters state updated:', parameters.map((p, i) => ({
      index: i,
      name: p.name,
      type: p.type,
      mode: p.mode,
      defaultValue: p.defaultValue
    })))
  }, [parameters])

  const addParameter = () => {
    setParameters([...parameters, { name: '', type: 'integer', mode: 'IN' }])
  }

  const removeParameter = (index: number) => {
    setParameters(parameters.filter((_, i) => i !== index))
  }

  const updateParameter = (index: number, field: keyof FunctionParameter, value: string) => {
    const newParameters = Array.from(parameters)
    newParameters[index] = { ...newParameters[index], [field]: value }
    console.log(`Updating parameter ${index}, field ${field} to value ${value}`)
    console.log('New parameter at index:', newParameters[index])
    setParameters(newParameters)
  }

  const alterFunctionMutation = useMutation({
    mutationFn: async () => {
      // Use custom return type if selected, otherwise use the dropdown value
      const finalReturnType = returnType === 'CUSTOM' ? customReturnType : returnType
      
      // Debug logging
      console.log('Altering function with parameters:', parameters)
      console.log('Parameters detail:', parameters.map((p, i) => `[${i}] name="${p.name}", type="${p.type}", mode="${p.mode}", default="${p.defaultValue}"`))
      console.log('Return type:', finalReturnType)
      console.log('Function body:', functionBody)
      console.log('Language:', language)
      
      // Build the complete function definition from individual fields
      const functionDefinition = buildFunctionDefinition(
        parameters,
        finalReturnType,
        functionBody,
        language,
        connection.type
      )
      
      console.log('Built function definition:', functionDefinition)
      
      // Match the working test case format exactly
      const commandData = {
        function_name: functionName,
        function_definition: functionDefinition,
        schema_name: schema || (connection.type === 'mysql' ? database : 'public'),
        database_name: database,
        returns: finalReturnType,
        body: functionBody,
        language: language
      }
      
      console.log('Matching test case format exactly')
      
      console.log('Command data being sent (OUT param workaround):', commandData)
      
      return AgentProxyService.alterFunction(
        connection.id,
        database,
        functionName,
        commandData
        // Don't pass schema separately since it's already in commandData
      )
    },
    onSuccess: () => {
      showToast('success', 'Function updated successfully')
      // Invalidate queries to refresh the function details
      void queryClient.invalidateQueries({
        queryKey: ['function-details', connection.id, database, functionName]
      })
      // Also invalidate the functions list to update the tree view
      void queryClient.invalidateQueries({
        queryKey: ['databases', 'functions', connection.id, database, schema]
      })
      onSuccess()
      onClose()
    },
    onError: (error: unknown) => {
      console.error('Failed to update function:', error)
      showToast('error', (error as Error).message || 'Failed to update function')
    }
  })

  const handleSave = async () => {
    console.log('=== handleSave called ===')
    console.log('Current parameters state:', parameters)
    console.log('Current language:', language)
    console.log('Current returnType:', returnType)
    console.log('Current functionBody length:', functionBody.length)
    
    if (!functionBody.trim()) {
      showToast('error', 'Function body is required')
      return
    }

    // Validate parameters
    for (const param of parameters) {
      if (!param.name.trim()) {
        showToast('error', 'All parameters must have a name')
        return
      }
    }

    setIsSaving(true)
    try {
      await alterFunctionMutation.mutateAsync()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 w-3/4 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <div className="text-2xl mr-3">⚙️</div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit Function: {functionName}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Modify the function definition and properties
            </p>
          </div>
        </div>
        <button type="button"
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6 max-w-4xl">
          {/* Basic Information */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Function Name
                </label>
                <input
                  type="text"
                  value={functionName}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                  placeholder="Function name cannot be changed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Return Type
                </label>
                {returnType === 'CUSTOM' ? (
                  <div className="space-y-2">
                    <select
                      value={returnType}
                      onChange={(e) => {
                        setReturnType(e.target.value)
                        if (e.target.value !== 'CUSTOM') {
                          setCustomReturnType('')
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {returnTypes.map((type) => (
                        <option key={type} value={type}>
                          {type === 'CUSTOM' ? 'Custom/TABLE' : type}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={customReturnType}
                      onChange={(e) => setCustomReturnType(e.target.value)}
                      placeholder="e.g., TABLE(id integer, name text) or custom_type"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  </div>
                ) : (
                  <select
                    value={returnType}
                    onChange={(e) => {
                      setReturnType(e.target.value)
                      if (e.target.value !== 'CUSTOM') {
                        setCustomReturnType('')
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {returnTypes.map((type) => (
                      <option key={type} value={type}>
                        {type === 'CUSTOM' ? 'Custom/TABLE' : type}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {availableLanguages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Schema
                </label>
                <input
                  type="text"
                  value={schema || (connection.type === 'mysql' ? database : 'public')}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                  placeholder="Schema cannot be changed"
                />
              </div>
            </div>
          </div>

          {/* Parameters */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Parameters
              </h3>
              <button
                type="button"
                onClick={addParameter}
                className="flex items-center px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add Parameter
              </button>
            </div>

            {parameters.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                No parameters defined. Click "Add Parameter" to add function parameters.
              </p>
            ) : (
              <div className="space-y-3">
                {parameters.map((parameter, index) => (
                  <div key={index} className="flex items-center gap-3">
                    {isDatabasePostgreSQL && (
                      <div className="w-20">
                        <select
                          value={parameter.mode || 'IN'}
                          onChange={(e) => updateParameter(index, 'mode', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        >
                          <option value="IN">IN</option>
                          <option value="OUT">OUT</option>
                          <option value="INOUT">INOUT</option>
                        </select>
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <input
                        type="text"
                        value={parameter.name}
                        onChange={(e) => updateParameter(index, 'name', e.target.value)}
                        placeholder="parameter_name"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        required
                      />
                    </div>
                    
                    <div className="w-32">
                      <select
                        value={parameter.type}
                        onChange={(e) => updateParameter(index, 'type', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      >
                        {commonDataTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="w-24">
                      <input
                        type="text"
                        value={parameter.defaultValue || ''}
                        onChange={(e) => updateParameter(index, 'defaultValue', e.target.value)}
                        placeholder="Default"
                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => removeParameter(index)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Function Body */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Function Body
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Function Logic *
              </label>
              <textarea
                value={functionBody}
                onChange={(e) => setFunctionBody(e.target.value)}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                placeholder={isDatabasePostgreSQL 
                  ? `BEGIN
    -- Your function logic here
    -- Use parameters like: parameter_name
    -- Return value using: RETURN result;
    
    DECLARE
        result INTEGER;
    BEGIN
        -- Example calculation
        result := parameter1 + parameter2;
        RETURN result;
    END;
END;`
                  : `-- Your function logic here
    -- Use parameters like: parameter_name
    -- Return value using: RETURN result;
    
    DECLARE result INT;
    SET result = parameter1 + parameter2;
    RETURN result;`
                }
                required
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {isDatabasePostgreSQL 
                  ? "Write the function body in PL/pgSQL. Use DECLARE for variables, BEGIN/END for blocks, and RETURN for return values."
                  : "Write the function body in SQL. Use DECLARE for variables and RETURN for return values."
                }
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start">
              <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Editing Guidelines:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Function name and schema cannot be changed during editing</li>
                  <li>Changes to parameters may require updating dependent code</li>
                  <li>Return type changes may affect calling code</li>
                  <li>Test your changes thoroughly before saving</li>
                  {isDatabasePostgreSQL && <li>PL/pgSQL supports variables, loops, conditionals, and exception handling</li>}
                  {isDatabaseMySQL && <li>MySQL functions must maintain DETERMINISTIC characteristics if specified</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button type="button"
          onClick={onClose}
          disabled={isSaving}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || !functionBody.trim()}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  )
}